import React from 'react';
import { Result, Button } from 'antd';
import { useTheme } from '../context/ThemeContext';
import { theme as antdTheme } from 'antd';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorDisplay error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

const ErrorDisplay: React.FC<{ 
  error: Error | null; 
  onReset: () => void;
}> = ({ error, onReset }) => {
  const { theme: currentTheme } = useTheme();
  const isDarkMode = currentTheme.algorithm?.[0] === antdTheme.darkAlgorithm;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isDarkMode ? '#141414' : '#f5f7fa'
    }}>
      <Result
        status="error"
        title="Oops!"
        subTitle={
          <div style={{ textAlign: 'center' }}>
            <p style={{ 
              fontSize: '16px',
              margin: '16px 0',
              color: isDarkMode ? '#ffffff99' : '#00000099'
            }}>
              {error?.message || 'An unexpected error occurred'}
            </p>
            <p style={{ 
              fontSize: '14px',
              margin: '8px 0',
              color: isDarkMode ? '#ffffff73' : '#00000073'
            }}>
              We're working on fixing this issue
            </p>
          </div>
        }
        style={{
          backgroundColor: isDarkMode ? '#1f1f1f' : '#ffffff',
          padding: '32px',
          borderRadius: '12px',
          maxWidth: '480px',
          width: '90%',
          margin: '20px',
          boxShadow: isDarkMode 
            ? '0 8px 24px rgba(0, 0, 0, 0.4)'
            : '0 8px 24px rgba(0, 0, 0, 0.1)'
        }}
        extra={
          <Button 
            type="primary" 
            onClick={onReset}
            size="large"
            style={{ minWidth: '120px' }}
          >
            Try Again
          </Button>
        }
      />
    </div>
  );
};

export default ErrorBoundary;